import React from 'react';
import { Box, Typography } from '@mui/material';
import { RingArc, RING_CIRCUMFERENCE, RING_RADIUS } from './RingArc';

// Blank space left at each junction so the two arcs read as separate wedges.
const SEGMENT_GAP = 10;

/**
 * The circular score indicator on the results page. A score of 100% (or 0%) is
 * a single unbroken ring; anything in between splits into a correct arc and an
 * incorrect arc with a gap at each junction.
 *
 * Props:
 *   id         (string) - ID prefix for generated element IDs.
 *   percentage (number) - The score, 0 to 100.
 */
export const ScoreRing = ({ id, percentage }) => {
  const correctLength = (percentage / 100) * RING_CIRCUMFERENCE;
  const incorrectLength = RING_CIRCUMFERENCE - correctLength;

  // One arc can be far shorter than the gap; shrink the gap so it still shows.
  const gap = Math.min(SEGMENT_GAP, Math.min(correctLength, incorrectLength) / 2);
  const isSplit = percentage > 0 && percentage < 100;

  return (
    <Box className="scoreRing" id={`${id}__ring`}>
      <svg viewBox="0 0 120 120" role="img" aria-label={`Score ${percentage} percent`}>
        {percentage === 100 && (
          <circle className="ringSegment correct" cx="60" cy="60" r={RING_RADIUS} />
        )}
        {percentage === 0 && (
          <circle className="ringSegment incorrect" cx="60" cy="60" r={RING_RADIUS} />
        )}
        {isSplit && (
          <>
            {/* Each arc is inset by half a gap at both ends, so the gaps match. */}
            <RingArc variant="correct" start={gap / 2} length={correctLength - gap} />
            <RingArc
              variant="incorrect"
              start={correctLength + gap / 2}
              length={incorrectLength - gap}
            />
          </>
        )}
      </svg>

      <Box className="scoreRingLabel">
        <Typography className="scorePercentage" id={`${id}__scorePercentage`}>
          {percentage}%
        </Typography>
      </Box>
    </Box>
  );
};
